import React, { useCallback, useMemo } from 'react';
import { Row, Col, Input } from 'antd';
import {
  ExportOutlined,
  FilterFilled,
  FilterOutlined,
  PlusOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import ScheduleDatePicker from './ScheduleDatePicker';
import ScheduleZoomButtons from './ScheduleZoomButtons';
import WhoseScheduledDrawer from './WhoseScheduledDrawer';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

import Permissions from '../auth/Permissions';
import { getViewDateRange } from './scheduleHelpers';
import { isFilterActive } from './GanttSchedule/ganttScheduleHelpers';
import BorderlessButton from '../common/buttons/BorderlessButton';

const BASE_GUTTER = 12;
const SIDEBAR_GUTTER = 5;

function ScheduleHeader({
  viewType,
  onViewTypeChange,
  onDateChange,
  date,
  onSearch,
  onAdd,
  onExport,
  enableHotkeys,
  disableHotkeys,
  openFilterDrawer,
  filters,
  isSideBarOpen,
  onSideBarOpen,
}) {
  const isActive = useMemo(() => isFilterActive(filters), [filters]);
  const onSearchChange = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    onSearch(value);
  }, [onSearch]);

  const onInputVisibleChanged = useCallback((visible) => {
    if (visible) {
      disableHotkeys();
    } else {
      enableHotkeys();
    }
  }, [enableHotkeys, disableHotkeys]);

  const { startDate, endDate } = getViewDateRange(date, viewType);

  const typeMap = {
    Day: 'date',
    Week: 'week',
    Month: 'month',
    UserDay: 'date',
    UserBiWeekly: 'week',
    UserWeekdays: 'week',
    UserWeek: 'week',
  };

  const gutter = isSideBarOpen ? SIDEBAR_GUTTER : BASE_GUTTER;

  return (
    <Row
      id="schedule-header-container"
      justify="space-between"
      align="middle"
      gutter={isSideBarOpen ? SIDEBAR_GUTTER : 20}
      style={{
        display: 'flex',
      }}
    >
      <Col style={{
        flexGrow: 1,
      }}
      >
        <Row justify="start" gutter={gutter}>
          {!isSideBarOpen && (
            <Col>
              <BorderlessButton
                iconNode={<VerticalRightOutlined />}
                onClick={onSideBarOpen}
                style={{ background: 'transparent', padding: 0 }}
              />
            </Col>
          )}
          <Col>
            <Input.Search
              className="searchbar"
              placeholder="Search"
              onChange={onSearchChange}
              allowClear
              onFocus={disableHotkeys}
              onBlur={enableHotkeys}
              style={{
                width: 125,
              }}
            />
          </Col>
          <Col>
            {Permissions.has('SCHEDULE_WRITE') && (
              <OnTraccrButton
                title="Add"
                icon={<PlusOutlined />}
                onClick={onAdd}
              />
            )}
          </Col>
          <Col>
            <WhoseScheduledDrawer startDate={startDate} endDate={endDate} />
          </Col>
          <Col>
            <OnTraccrButton
              title="Export"
              icon={<ExportOutlined />}
              onClick={onExport}
            />
          </Col>
        </Row>
      </Col>
      <Col style={{
        flexGrow: 1,
      }}
      >
        <Row justify="end" gutter={gutter}>
          <Col>
            <OnTraccrButton
              type="cancel"
              icon={
                  isActive
                    ? <FilterFilled style={{ marginLeft: 0 }} />
                    : <FilterOutlined style={{ marginLeft: 0 }} />
                }
              onClick={openFilterDrawer}
            />
          </Col>
          <Col>
            <ScheduleDatePicker
              viewType={viewType}
              onViewTypeChange={onViewTypeChange}
              onDateChange={onDateChange}
              date={date}
              onVisibleChanged={onInputVisibleChanged}
              typeMap={typeMap}
            />
          </Col>
          <Col>
            {viewType !== 'Month' && viewType !== 'UserBiWeekly' && <ScheduleZoomButtons />}
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

/* eslint-disable react/forbid-prop-types */
ScheduleHeader.propTypes = {
  viewType: PropTypes.string.isRequired,
  onViewTypeChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(DateTime).isRequired,
  onSearch: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  enableHotkeys: PropTypes.func.isRequired,
  disableHotkeys: PropTypes.func.isRequired,
  openFilterDrawer: PropTypes.func.isRequired,
  filters: PropTypes.shape({}),
  isSideBarOpen: PropTypes.bool.isRequired,
  onSideBarOpen: PropTypes.func.isRequired,
};

ScheduleHeader.defaultProps = {
  filters: {},
};

export default ScheduleHeader;
